import React from "react";
import "./index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { hasWorkspaceAccess, type WorkspaceId } from "./lib/workspaces";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppShell } from "./components/layout/AppShell";
import Login from "./pages/auth/Login";
import MfaSetup from "./pages/auth/MfaSetup";
import ChangePassword from "./pages/auth/ChangePassword";
import Home from "./pages/Home";
import Tenants from "./pages/platform/Tenants";
import TenantDetail from "./pages/platform/TenantDetail";
import PlatformDashboard from "./pages/platform/PlatformDashboard";
import SupportAccess from "./pages/platform/SupportAccess";
import PlatformBilling from "./pages/platform/PlatformBilling";
import Contacts from "./pages/crm/Contacts";
import ContactDetail from "./pages/crm/ContactDetail";
import Organizations from "./pages/crm/Organizations";
import RelationshipContacts from "./pages/crm/RelationshipContacts";
import OsaResults from "./pages/crm/OsaResults";
import StaffInvoices from "./pages/tenant/StaffInvoices";
import OpportunitiesKanban from "./pages/crm/OpportunitiesKanban";
import OpportunityDetail from "./pages/crm/OpportunityDetail";
import CalendarPage from "./pages/crm/Calendar";
import PatientCalendar from "./pages/crm/PatientCalendar";
import CoachingNotes from "./pages/crm/CoachingNotes";
import ComplaintCases from "./pages/tenant/ComplaintCases";
import SuggestionBox from "./pages/tenant/SuggestionBox";
import ConfigViewer from "./pages/config/ConfigViewer";
import RelationshipTypes from "./pages/config/RelationshipTypes";
import WorkflowEditor from "./pages/config/WorkflowEditor";
import AuditLog from "./pages/audit/AuditLog";
import TenantStructure from "./pages/tenant/Structure";
import TenantUsers from "./pages/tenant/TenantUsers";
import StaffOffboarding from "./pages/tenant/StaffOffboarding";
import MyPerformance from "./pages/tenant/MyPerformance";
import User360Workspace from "./pages/tenant/User360Workspace";
import CommissionCenter from "./pages/tenant/CommissionCenter";
import Memberships from "./pages/tenant/Memberships";
import ImportsIndex from "./pages/imports/ImportsIndex";
import ImportWizard from "./pages/imports/ImportWizard";
import OnboardingWorkspace from "./pages/onboarding/OnboardingWorkspace";
import Dashboard from "./pages/tenant/Dashboard";
import { MyDayDashboard } from "./components/domain/MyDayDashboard";
import Tasks from "./pages/crm/Tasks";
import VisitVerification from "./pages/crm/VisitVerification";
import Notifications from "./pages/tenant/Notifications";
import Reports from "./pages/tenant/Reports";
import ExpenseClaims from "./pages/tenant/ExpenseClaims";
import Training from "./pages/tenant/Training";
import Equipment from "./pages/tenant/Equipment";
import StaffRequests from "./pages/tenant/StaffRequests";
import Announcements from "./pages/tenant/Announcements";
import DataQuality from "./pages/tenant/DataQuality";
import Referrals from "./pages/crm/Referrals";
import ReferralStatusReport from "./pages/crm/ReferralStatusReport";
import Territories from "./pages/tenant/Territories";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-sm text-slate-500">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function WorkspaceRoute({ workspace, children }: { workspace: WorkspaceId; children: React.ReactNode }) {
  const { user } = useAuth();
  if (user && !hasWorkspaceAccess(user, workspace)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function ReportsCenterRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user && !hasWorkspaceAccess(user, "reports") && !hasWorkspaceAccess(user, "referrals")) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function App() {
  return (
    <AuthProvider>
      <Toaster richColors position="top-right" />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/mfa-setup" element={<RequireAuth><MfaSetup /></RequireAuth>} />
          <Route path="/change-password" element={<RequireAuth><ChangePassword /></RequireAuth>} />
          <Route element={<AppShell />}>
            <Route path="/" element={<Home />} />
            <Route path="/dashboard" element={<WorkspaceRoute workspace="dashboard"><Dashboard /></WorkspaceRoute>} />
            <Route path="/my-day" element={<WorkspaceRoute workspace="my_day"><MyDayDashboard /></WorkspaceRoute>} />
            <Route path="/tasks" element={<WorkspaceRoute workspace="tasks"><Tasks /></WorkspaceRoute>} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/reports" element={<ReportsCenterRoute><Reports /></ReportsCenterRoute>} />
            <Route path="/reports/referrals" element={<WorkspaceRoute workspace="referrals"><ReferralStatusReport /></WorkspaceRoute>} />
            <Route path="/expenses" element={<WorkspaceRoute workspace="expenses"><ExpenseClaims /></WorkspaceRoute>} />
            <Route path="/training" element={<WorkspaceRoute workspace="training"><Training /></WorkspaceRoute>} />
            <Route path="/equipment" element={<Equipment />} />
            <Route path="/staff-requests" element={<StaffRequests />} />
            <Route path="/announcements" element={<Announcements />} />
            <Route path="/data-quality" element={<DataQuality />} />
            <Route path="/platform" element={<PlatformDashboard />} />
            <Route path="/platform/tenants" element={<Tenants />} />
            <Route path="/platform/tenants/:tenantId" element={<TenantDetail />} />
            <Route path="/platform/support-access" element={<SupportAccess />} />
            <Route path="/platform/billing" element={<PlatformBilling />} />
            <Route path="/platform/announcements" element={<Announcements />} />
            <Route path="/contacts" element={<WorkspaceRoute workspace="contacts"><Contacts /></WorkspaceRoute>} />
            <Route path="/contacts/:contactId" element={<WorkspaceRoute workspace="contacts"><ContactDetail /></WorkspaceRoute>} />
            <Route path="/referrals" element={<WorkspaceRoute workspace="referrals"><Referrals /></WorkspaceRoute>} />
            <Route path="/referrals/report" element={<Navigate to="/reports/referrals" replace />} />
            <Route path="/organizations" element={<WorkspaceRoute workspace="organizations"><Organizations /></WorkspaceRoute>} />
            <Route path="/relationship-contacts" element={<WorkspaceRoute workspace="relationship_contacts"><RelationshipContacts /></WorkspaceRoute>} />
            <Route path="/osa-results" element={<WorkspaceRoute workspace="osa_results"><OsaResults /></WorkspaceRoute>} />
            <Route path="/staff-invoices" element={<WorkspaceRoute workspace="staff_invoices"><StaffInvoices /></WorkspaceRoute>} />
            <Route path="/opportunities" element={<WorkspaceRoute workspace="opportunities"><OpportunitiesKanban /></WorkspaceRoute>} />
            <Route path="/opportunities/:opportunityId" element={<WorkspaceRoute workspace="opportunities"><OpportunityDetail /></WorkspaceRoute>} />
            <Route path="/calendar" element={<WorkspaceRoute workspace="calendar"><CalendarPage /></WorkspaceRoute>} />
            <Route path="/patient-calendar" element={<PatientCalendar />} />
            <Route path="/visit-verification" element={<WorkspaceRoute workspace="visit_verification"><VisitVerification /></WorkspaceRoute>} />
            <Route path="/coaching" element={<WorkspaceRoute workspace="coaching"><CoachingNotes /></WorkspaceRoute>} />
            <Route path="/complaints" element={<WorkspaceRoute workspace="complaints"><ComplaintCases /></WorkspaceRoute>} />
            <Route path="/suggestions" element={<WorkspaceRoute workspace="suggestions"><SuggestionBox /></WorkspaceRoute>} />
            <Route path="/my-performance" element={<WorkspaceRoute workspace="my_performance"><MyPerformance /></WorkspaceRoute>} />
            <Route path="/admin/users/:userId/360" element={<User360Workspace />} />
            <Route path="/user-360/:userId?" element={<WorkspaceRoute workspace="user_360"><User360Workspace /></WorkspaceRoute>} />
            <Route path="/admin/commissions" element={<CommissionCenter />} />
            <Route path="/config" element={<ConfigViewer />} />
            <Route path="/config/relationship-types" element={<RelationshipTypes />} />
            <Route path="/config/workflow" element={<WorkflowEditor />} />
            <Route path="/audit" element={<AuditLog />} />
            <Route path="/admin/users" element={<TenantUsers />} />
            <Route path="/admin/users/:userId/offboard" element={<StaffOffboarding />} />
            <Route path="/admin/structure" element={<TenantStructure />} />
            <Route path="/admin/memberships" element={<Memberships />} />
            <Route path="/admin/territories" element={<Territories />} />
            <Route path="/imports" element={<WorkspaceRoute workspace="imports"><ImportsIndex /></WorkspaceRoute>} />
            <Route path="/imports/new" element={<WorkspaceRoute workspace="imports"><ImportWizard /></WorkspaceRoute>} />
            <Route path="/imports/:jobId" element={<WorkspaceRoute workspace="imports"><ImportWizard /></WorkspaceRoute>} />
            <Route path="/onboarding" element={<OnboardingWorkspace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
